/**
 * Overlay de diagnóstico sin DevTools.
 * Dev-only por contrato: en producción `isAuthDebugUiEnabled()` siempre devuelve false.
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAuthDebugLatestRedirectReason,
  getAuthDebugLogEntries,
  getAuthDebugOverlaySlices,
  isAuthDebugUiEnabled,
  subscribeAuthDebug,
  type AuthDebugLogEntry,
} from '@/lib/authDebugInstrumentation';

function useForceUpdate(): () => void {
  const [, dispatch] = useReducer((counter: number) => counter + 1, 0);
  return useCallback(() => {
    dispatch();
  }, []);
}

/** Recarga estado de UI en localStorage o ?debugAuthUi=1 si el mismo tab activa después de cargar */
function useAuthDebugActivationPulse(): boolean {
  const [, dispatch] = useReducer((counter: number) => counter + 1, 0);

  useEffect(() => {
    const tick = (): void => {
      dispatch();
    };

    const intervalId = window.setInterval(tick, 5000);

    window.addEventListener('popstate', tick);
    window.addEventListener('pageshow', tick);

    const onStorage = (event: StorageEvent): void => {
      if (event.key === 'rc_auth_debug_ui') {
        dispatch();
      }
    };
    window.addEventListener('storage', onStorage);

    return (): void => {
      window.clearInterval(intervalId);
      window.removeEventListener('popstate', tick);
      window.removeEventListener('pageshow', tick);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return isAuthDebugUiEnabled();
}

async function writeClipboardSafe(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Safari / permisos: fallback
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-10px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return Boolean(ok);
  } catch {
    return false;
  }
}

export function AuthDebugOverlayGate(): React.ReactNode {
  const overlayEnabled = useAuthDebugActivationPulse();
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    if (!overlayEnabled) return undefined;
    return subscribeAuthDebug(forceUpdate);
  }, [forceUpdate, overlayEnabled]);

  if (!overlayEnabled) return null;

  return <AuthDebugOverlay />;
}

export function AuthDebugOverlay(): React.ReactNode {
  const location = useLocation();
  const forceUpdate = useForceUpdate();

  useEffect(() => subscribeAuthDebug(forceUpdate), [forceUpdate]);

  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const bumpToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2500);
  }, []);

  const logs = getAuthDebugLogEntries();
  const visibleLogs = logs.length > 60 ? logs.slice(-60) : logs;
  const overlaySlices = getAuthDebugOverlaySlices();
  const redirectReason = getAuthDebugLatestRedirectReason();

  const adminSlice =
    overlaySlices.AdminProtectedRoute && typeof overlaySlices.AdminProtectedRoute === 'object'
      ? overlaySlices.AdminProtectedRoute
      : undefined;
  const loginSlice =
    overlaySlices.PublicLogin && typeof overlaySlices.PublicLogin === 'object'
      ? overlaySlices.PublicLogin
      : undefined;

  const logTextBlock = useMemo(() => {
    return logs
      .map((entry) => {
        const iso = new Date(entry.epochMs).toISOString();
        const qs = entry.searchSnapshot ? `${entry.pathnameSnapshot}${entry.searchSnapshot}` : entry.pathnameSnapshot;
        const payloadPretty = serializePayloadBrief(entry.payload);
        return `[${iso}] ${entry.event} path=${qs} ${payloadPretty}`;
      })
      .join('\n');
  }, [logs]);

  const exportJson = useMemo(() => {
    return JSON.stringify(
      {
        exportedAtIso: new Date().toISOString(),
        router: {
          pathname: location.pathname,
          search:
            typeof location.search === 'string' && location.search.length > 256
              ? `${location.search.slice(0, 256)}…`
              : location.search ?? '',
          hashPresent: typeof location.hash === 'string' && location.hash.length > 0,
        },
        latestRedirectReason: redirectReason,
        overlaySlices,
        logs: [...logs],
      },
      null,
      2,
    );
  }, [location.pathname, location.search, location.hash, overlaySlices, logs, redirectReason]);

  const handleCopyLogs = (): void => {
    void (async (): Promise<void> => {
      const ok = await writeClipboardSafe(logTextBlock);
      bumpToast(ok ? 'Logs copiados' : 'No se pudo copiar logs');
    })();
  };

  const handleCopyJson = (): void => {
    void (async (): Promise<void> => {
      const ok = await writeClipboardSafe(exportJson);
      bumpToast(ok ? 'JSON copiado' : 'No se pudo copiar JSON');
    })();
  };

  const handleDownloadJson = (): void => {
    try {
      const blob = new Blob([exportJson], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `rc-auth-debug-${Date.now()}.json`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      bumpToast('JSON descargado');
    } catch {
      bumpToast('Descarga JSON fallida; usa Copiar JSON');
    }
  };

  return (
    <div
      className={
        expanded
          ? 'fixed inset-x-2 bottom-2 z-[2147483000] max-h-[min(480px,calc(100vh-140px))] flex flex-col rounded-xl border border-amber-500/70 bg-black/93 text-[11px] text-amber-100 shadow-xl backdrop-blur-sm sm:inset-auto sm:right-3 sm:left-auto sm:w-[min(460px,calc(100vw-24px))] sm:bottom-6'
          : 'fixed bottom-3 right-3 z-[2147483000] flex items-center rounded-full border border-amber-500/70 bg-black/93 px-2 py-1 text-[11px] text-amber-100 shadow-xl'
      }
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}
      aria-label="Instrumentación temporal de diagnóstico auth"
      role="region"
    >
      <div className="flex shrink-0 items-center justify-between gap-1 border-b border-amber-500/35 px-2 py-1.5 sm:px-3">
        <button
          type="button"
          className="min-h-[44px] min-w-[44px] flex-1 touch-manipulation px-2 text-left font-semibold uppercase tracking-wide text-amber-200"
          onClick={() => setExpanded((prev) => !prev)}
        >
          RC auth debug {!expanded ? `(${logs.length})` : null}
        </button>
        {expanded ? (
          <div className="flex flex-wrap items-center justify-end gap-1">
            <button
              type="button"
              className="rounded-md border border-amber-500/50 bg-black/70 px-2 py-2 text-[10px] font-medium text-amber-100 touch-manipulation"
              onClick={handleCopyLogs}
            >
              Copiar logs
            </button>
            <button
              type="button"
              className="rounded-md border border-amber-500/50 bg-black/70 px-2 py-2 text-[10px] font-medium text-amber-100 touch-manipulation"
              onClick={handleCopyJson}
            >
              Copiar JSON
            </button>
            <button
              type="button"
              className="rounded-md border border-amber-500/50 bg-black/70 px-2 py-2 text-[10px] font-medium text-amber-100 touch-manipulation"
              onClick={handleDownloadJson}
            >
              Descargar
            </button>
          </div>
        ) : null}
      </div>

      {toast ? (
        <div className="shrink-0 border-b border-amber-600/45 bg-amber-900/55 px-2 py-1 text-center text-[10px] text-amber-50">
          {toast}
        </div>
      ) : null}

      {expanded ? (
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden px-2 pb-2 sm:px-3">
          <div className="shrink-0 space-y-0.5 rounded-md border border-white/15 bg-black/60 px-2 py-1.5 leading-snug">
            <div className="font-semibold text-amber-300">Ubicación (router)</div>
            <div className="break-all text-emerald-200/95">
              {location.pathname}
              <span className="text-emerald-300/80">{trimSearch(location.search)}</span>
            </div>
            <div className="font-semibold text-amber-300">Último redirect (inferido)</div>
            <div className="break-all text-orange-100/95">{redirectReason ?? '(ninguno registrado)'}</div>
          </div>

          <div className="shrink-0 space-y-0.5 rounded-md border border-white/15 bg-black/60 px-2 py-1.5">
            <div className="font-semibold text-amber-300">Flags guard (snapshot)</div>
            {adminSlice ? <FlagGrid slice={adminSlice} /> : <div className="text-white/65">Sin snapshot admin (¿no montado?)</div>}
          </div>

          <div className="shrink-0 space-y-0.5 rounded-md border border-white/15 bg-black/60 px-2 py-1.5">
            <div className="font-semibold text-amber-300">Login / MFA overlay</div>
            {loginSlice ? <FlagGrid slice={loginSlice} /> : <div className="text-white/65">Sin slice PublicLogin aún.</div>}
          </div>

          <div className="min-h-0 flex flex-1 flex-col overflow-hidden rounded-md border border-white/15 bg-black/70">
            <div className="shrink-0 border-b border-white/10 px-2 py-1 font-semibold text-amber-300">
          Eventos ({logs.length}; mostrando {visibleLogs.length})
            </div>
            <div className="min-h-[120px] flex-1 overflow-y-auto overscroll-contain px-1 py-1 font-mono text-[10px] leading-snug text-emerald-100/90 touch-pan-y">
              {logs.length === 0 ? (
                <div className="text-white/50">Sin eventos aún. Navega o inicia sesión.</div>
              ) : (
                [...visibleLogs]
                  .reverse()
                  .map((entry) => <LogRow key={entry.id} entry={entry} />)
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function trimSearch(search: string): string {
  if (!search || search === '') return '';
  return search.length > 120 ? `${search.slice(0, 118)}…` : search;
}

function serializePayloadBrief(payload: Record<string, unknown>): string {
  try {
    const keys = Object.keys(payload);
    if (keys.length === 0) return '';
    const short: Record<string, unknown> = {};
    for (const key of keys) {
      short[key] = payload[key];
    }
    let line = JSON.stringify(short);
    if (line.length > 220) {
      line = `${line.slice(0, 217)}…`;
    }
    return line;
  } catch {
    return '';
  }
}

function FlagGrid({ slice }: { slice: Record<string, unknown> }): React.ReactElement {
  const keys = [...Object.keys(slice)].sort();

  const visible = keys.length > 18 ? keys.slice(0, 18) : keys;

  return (
    <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
      {visible.map((key) => (
        <div key={key} className="flex items-baseline gap-1 break-all">
          <span className="shrink-0 text-[9px] uppercase text-white/45">{formatKeyShort(key)}</span>
          <span className="text-[10px] text-teal-200/95">{formatDisplayValue(slice[key])}</span>
        </div>
      ))}
    </div>
  );
}

function formatKeyShort(key: string): string {
  const mapFlag: Record<string, string> = {
    loadingAuth: 'ldAuth',
    loadingAccess: 'ldAcc',
    loadingMfa: 'ldMfa',
    loadingPanelSession: 'ldPanel',
    loadingVerificationGate: 'ldGate',
    shouldCheckPanelSession: 'chkPanel',
    hasValidPanelSession: 'validPanel',
    needsEnrollment: 'enroll?',
    needsChallenge: 'chal?',
    blockingLoader: 'block?',
    hasAdminAccess: 'adm?',
  };
  return mapFlag[key] ?? key;
}

function formatDisplayValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number' || typeof value === 'string') return String(value).slice(0, 140);
  if (typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value);
      return serialized.length > 120 ? `${serialized.slice(0, 117)}…` : serialized;
    } catch {
      return '[object]';
    }
  }
  return '?';
}

function LogRow({ entry }: { entry: AuthDebugLogEntry }): React.ReactElement {
  const pathLine = `${entry.pathnameSnapshot}${entry.searchSnapshot}`;
  const time = `${new Date(entry.epochMs).toISOString()} +${Math.round(entry.perfMsApprox)}ms`;

  return (
    <div className="mb-1 rounded border border-white/10 bg-white/5 px-1 py-1">
      <div className="text-[9px] text-white/50">{time}</div>
      <div className="font-semibold text-amber-200/95">{entry.event}</div>
      <div className="break-all text-[9px] text-sky-200/85">{trimSearch(pathLine)}</div>
      {serializePayloadBrief(entry.payload) ? (
        <div className="break-all text-[9px] text-emerald-200/85">{serializePayloadBrief(entry.payload)}</div>
      ) : null}
    </div>
  );
}
